import { isRight } from "fp-ts/lib/Either";
import * as iots from "io-ts";
import { compareStrings } from "../support";
import { DateFormat, makeDateTimeFormat, TimeFormat } from "./format-date-time";
import {
    convertDateFromTimeZoneAgnostic,
    makeDateFromString,
    makeEndOfDay,
    makeEndOfDayTimeZoneAgnostic,
    makeStartOfDay,
    makeStartOfDayTimeZoneAgnostic,
} from "./support";

export const glideDateTimeDocumentDataCodec = iots.intersection([
    iots.type({
        kind: iots.literal("glide-date-time"),
        // This is the `x` in `new Date(x)`
        value: iots.number,
    }),
    iots.partial({
        // The date in whatever format the user chose in their spreadsheet.
        // We don't parse this, it's only used for display.
        repr: iots.string,
        // If this is missing, the date is time-zone agnostic.  If it's
        // present, the data is in UTC, and this is the time-zone offset (as
        // returned by `date.getTimeZoneOffset()`) from wherever the date
        // originated.  We're not actually using this offset right now, other
        // than to check for its presence.
        tzOffset: iots.number,
    }),
]);

export type GlideDateTimeDocumentData = iots.TypeOf<typeof glideDateTimeDocumentDataCodec>;

export function isGlideDateTimeDocumentData(x: unknown): x is GlideDateTimeDocumentData {
    return isRight(glideDateTimeDocumentDataCodec.decode(x));
}

const dummyDate = new Date();
function getTimeZoneOffset(): number {
    return dummyDate.getTimezoneOffset();
}

export class GlideDateTime {
    private constructor(private readonly _docData: GlideDateTimeDocumentData) {}

    public get isTimeZoneAware(): boolean {
        return this._docData.tzOffset !== undefined;
    }

    public static fromDocumentData(d: GlideDateTimeDocumentData): GlideDateTime {
        return new GlideDateTime(d);
    }

    public static fromTimeZoneAgnosticValue(value: number): GlideDateTime {
        return new GlideDateTime({
            kind: "glide-date-time",
            value,
        });
    }

    public static fromTimeZoneAwareValue(value: number): GlideDateTime {
        return new GlideDateTime({
            kind: "glide-date-time",
            value,
            tzOffset: getTimeZoneOffset(),
        });
    }

    public static fromTimeZoneAwareDate(d: Date): GlideDateTime {
        return GlideDateTime.fromTimeZoneAwareValue(d.getTime());
    }

    public static fromTimeZoneAgnosticDate(d: Date): GlideDateTime {
        return GlideDateTime.fromTimeZoneAgnosticValue(d.getTime());
    }

    public static now(): GlideDateTime {
        return GlideDateTime.fromTimeZoneAwareValue(Date.now());
    }

    private getTimeZoneAgnosticValue(): number {
        if (this._docData.tzOffset !== undefined) {
            return this._docData.value - this._docData.tzOffset * 60 * 1000;
        } else {
            return this._docData.value;
        }
    }

    // This is what we would use to write the date to Google Sheets, for
    // example.  This will return the date from the point of view of the
    // creator, not the viewer, i.e. if it was 2pm in the creator's local
    // timezone, this will return 2pm GMT, even if in our local timezone it
    // was 3pm.  This is compatible with how time-zone agnostic dates work. At
    // this point we should only have to use this in the OCM and on the
    // backend when writing to data sources that don't support time zones.
    // There's one exception: we use it for date math in NCM where it's
    // convenient.
    public asTimeZoneAgnosticDate(): Date {
        return new Date(this.getTimeZoneAgnosticValue());
    }

    // We need to ensure that
    //
    //   D === fromTimeZoneAwareValue(D.getTimeZoneAwareValue())
    //
    // even if D is time zone agnostic
    public getTimeZoneAwareValue(): number {
        if (this._docData.tzOffset !== undefined) {
            return this._docData.value;
        } else {
            // Let's say our time-zone agnostic value is 2am, and we're in
            // GMT-1.  The time-zone aware value will be 3am GMT, because
            // that will display as 2am in GMT-1
            return this._docData.value + getTimeZoneOffset() * 60 * 1000;
        }
    }

    public asTimeZoneAwareDate(): Date {
        return new Date(this.getTimeZoneAwareValue());
    }

    public toTimeZoneAgnostic(): GlideDateTime {
        if (this._docData.tzOffset === undefined) {
            return this;
        } else {
            return GlideDateTime.fromTimeZoneAgnosticValue(this.getTimeZoneAgnosticValue());
        }
    }

    public transformTimeZoneAwareValue(f: (v: number) => number): GlideDateTime {
        return new GlideDateTime({
            kind: "glide-date-time",
            value: f(this._docData.value),
            tzOffset: this._docData.tzOffset,
        });
    }

    // Should give you the start of day in whichever time zone you're in.
    public startOfDay(): GlideDateTime {
        // FIXME: Implement efficiently
        if (this._docData.tzOffset !== undefined) {
            // Note the we're not carrying over the `tzOffset` of the original
            // date, because it's now bound to the local time zone.  Also not
            // that we're getting the time-zone agnostic date here, not the
            // aware one, because the aware one might land us on the wrong
            // day.
            return GlideDateTime.fromTimeZoneAwareDate(makeStartOfDay(this.asTimeZoneAgnosticDate()));
        } else {
            return GlideDateTime.fromTimeZoneAgnosticDate(
                makeStartOfDayTimeZoneAgnostic(this.asTimeZoneAgnosticDate())
            );
        }
    }

    public endOfDay(): GlideDateTime {
        // FIXME: Implement efficiently
        if (this._docData.tzOffset !== undefined) {
            return GlideDateTime.fromTimeZoneAwareDate(makeEndOfDay(this.asTimeZoneAwareDate()));
        } else {
            return GlideDateTime.fromTimeZoneAgnosticDate(makeEndOfDayTimeZoneAgnostic(this.asTimeZoneAgnosticDate()));
        }
    }

    public format(dateFormat: DateFormat | undefined, timeFormat: TimeFormat | undefined): string {
        return makeDateTimeFormat(dateFormat, timeFormat).format(this.asTimeZoneAwareDate());
    }

    public asString(): string {
        const { repr } = this._docData;
        if (repr !== undefined) return repr;
        return this.format(DateFormat.Short, TimeFormat.WithSeconds);
    }

    public clone(): GlideDateTime {
        return GlideDateTime.fromDocumentData({ ...this._docData });
    }

    public toDocumentData(): GlideDateTimeDocumentData {
        return { ...this._docData };
    }

    public compareTo(other: unknown): number {
        // We compare time-zone aware values, because those are what the user
        // would see displayed.
        const leftValue = this.getTimeZoneAwareValue();
        let rightValue: number;

        if (other instanceof GlideDateTime) {
            rightValue = other.getTimeZoneAwareValue();
        } else if (other instanceof Date) {
            rightValue = convertDateFromTimeZoneAgnostic(other).getTime();
        } else if (typeof other === "string") {
            const { repr } = this._docData;

            // This is all very nasty but our users love to mix strings and dates.
            if (repr !== undefined) {
                // Special case for string comparison when repr? is available;
                // we only will end up dropping repr? when a displayFormula is
                // set on the appropriate column.
                if (repr === other) return 0;

                // If they aren't equal then we'll try parsing the date.
                const otherAsDate = makeDateFromString(other, true);

                if (otherAsDate === undefined) {
                    return compareStrings(repr, other);
                }

                rightValue = otherAsDate.getTime();
            } else {
                const otherAsDate = makeDateFromString(other, true);
                if (otherAsDate === undefined) {
                    return 1;
                }

                rightValue = otherAsDate.getTime();
            }
        } else {
            if (leftValue === other) return 0;
            // This follows the convention of Array.sort
            return leftValue < (other as any) ? -1 : 1;
        }

        return leftValue - rightValue;
    }
}

export function convertGlideDateTime<T>(d: T): T | GlideDateTime {
    if (!isGlideDateTimeDocumentData(d)) return d;
    return GlideDateTime.fromDocumentData(d);
}

export function forceGlideDateTime<T>(d: T | Date): T | GlideDateTime {
    if (d instanceof Date) {
        return GlideDateTime.fromTimeZoneAgnosticDate(d);
    } else {
        return d;
    }
}
